import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useInvestigationBill } from './investigation-bill.queries';
import { currency, formatInvestigationDateTime } from './investigation-bill.utils';

const BILL_HEADER_NAME = 'bill';

export const InvestigationBillPrintPage = () => {
  const { id } = useParams();
  const hospitalTitle = useHospitalTitle();
  const query = useInvestigationBill(id);
  const headerLogoQuery = useDocumentHeaderLogo(BILL_HEADER_NAME);

  useEffect(() => {
    if (query.data && headerLogoQuery.isFetched) window.setTimeout(() => window.print(), 300);
  }, [query.data, headerLogoQuery.isFetched]);

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Investigation bill not found" message="The bill could not be loaded." />;

  const row = query.data;
  const patient = row.patientSnapshot;
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  const logo = headerLogoQuery.data;

  return (
    <div className="print-bill-page">
      {logo ? <div className="print-bill-logo"><img src={logo} alt={hospitalTitle} /></div> : null}

      <table className="print-bill-header">
        <tbody>
          <tr>
            <td colSpan={2} className="print-bill-title">Bill Details ( Investigation Patient )</td>
          </tr>
          <tr>
            <td>
              <p className="print-bill-patient-name">Patient Name : {patient.name}</p>
              <p><b>UHID: </b>{patient.uhid ?? '-'}</p>
              <p><b>Age :</b> {age} &nbsp;&nbsp;&nbsp;&nbsp; <b>Sex :</b> {patient.gender}</p>
              <p><b>Address :</b> {address}</p>
              <p><b>Mobile No:</b> {patient.phone ?? '-'}</p>
            </td>
            <td>
              <p><b>Bill No. &amp; Date: </b>{row.billing.billNumber} <b>&amp;</b> {formatInvestigationDateTime(row.billDate)}</p>
              <p><b>Investigation No:</b> {row.investigationNumber}</p>
              <p><b>Referring Doctor:</b> {doctorNameOrDash(row.referringDoctor?.name)}</p>
              <p><b>Department:</b> {row.department?.name ?? '-'}</p>
              <p><b>Referred By :</b> {row.referredBy?.name ?? '-'}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="print-bill-charges">
        <thead>
          <tr>
            <th className="print-bill-col-index">#</th>
            <th>Date</th>
            <th>Particulars</th>
            <th className="print-bill-col-num">Qty</th>
            <th className="print-bill-col-num">Rate</th>
            <th className="print-bill-col-num">Dis.</th>
            <th className="print-bill-col-num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {row.charges.length === 0 ? (
            <tr>
              <td colSpan={7}>No itemized charge rows were stored for this bill.</td>
            </tr>
          ) : row.charges.map((line, index) => (
            <tr key={`${line.charge}-${index}`}>
              <td>{index + 1}</td>
              <td>{formatInvestigationDateTime(row.billDate)}</td>
              <td>{line.chargeName}</td>
              <td className="print-bill-col-num">{line.quantity}</td>
              <td className="print-bill-col-num">{line.rate}</td>
              <td className="print-bill-col-num">{line.discountAmount}</td>
              <td className="print-bill-col-num">{line.amount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6} className="print-bill-col-num">Sub Total</th>
            <th className="print-bill-col-num">{row.billing.total}</th>
          </tr>
        </tfoot>
      </table>

      <table className="print-bill-totals">
        <tbody>
          <tr>
            <td>TOTAL :</td>
            <td className="print-bill-col-num">{currency(row.billing.total)}</td>
          </tr>
          <tr>
            <td>NET AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.total)}</td>
          </tr>
          <tr>
            <td>DISCOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.totalDiscount)}</td>
          </tr>
          <tr>
            <td>TOTAL PAYABLE AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.grandTotal)}</td>
          </tr>
          <tr>
            <td>TOTAL PAID AMOUNT :</td>
            <td className="print-bill-col-num">{currency(row.billing.totalPayment)}</td>
          </tr>
          {row.billing.dueAmount > 0 ? (
            <tr className="print-bill-due-row">
              <td>DUE AMOUNT :</td>
              <td className="print-bill-col-num">{currency(row.billing.dueAmount)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="print-bill-signoff">
        <span>PREPARED BY: {row.generatedBy.name}</span>
        <span>Date: {formatInvestigationDateTime(new Date().toISOString())}</span>
      </div>
    </div>
  );
};
